import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, User, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createNotification } from '@/lib/notifications';
import InterviewForm from '@/components/interviews/interview-form';

interface JobApplicationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: any;
  onApplicationSubmitted?: () => void;
}

export default function JobApplicationForm({ 
  open, 
  onOpenChange, 
  job,
  onApplicationSubmitted
}: JobApplicationFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading ] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [interviewFormOpen, setInterviewFormOpen] = useState(false);

  // Check if user has already applied to this job
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  
  // Check application status and fetch profile when the dialog opens
  useEffect(() => {
    if (open && user && job) {
      checkApplicationStatus();
      fetchUserProfile();
      // Reset success state when dialog opens
      setApplicationSuccess(false);
    }
  }, [open, user, job]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (!error && data) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const checkApplicationStatus = async () => {
    if (!user || !job) return;
    
    setCheckingStatus(true);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('id')
        .eq('profile_id', user.id)
        .eq('job_id', job.id)
        .single();
        
      if (!error && data) {
        setHasApplied(true);
      } else {
        setHasApplied(false);
      }
    } catch (error) {
      console.error('Error checking application status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!user || !job) return;
    
    setIsLoading(true);
    try {
      // Create application record
      const { data: applicationData, error } = await supabase
        .from('applications')
        .insert([
          {
            profile_id: user.id,
            job_id: job.id,
            status: 'new',
            application_date: new Date().toISOString(),
          }
        ])
        .select('id')
        .single();
        
      if (error) throw error;
      
      // Send notification to job poster
      await createNotification({
        profileId: job.profile_id,
        type: 'opportunity',
        title: 'New Job Application',
        description: `Someone has applied to your "${job.title}" position`,
        actionUrl: `/dashboard/applications/${job.id}`,
        relatedEntityId: applicationData.id,
        relatedEntityType: 'job_application'
      });
      
      // Show success state
      setApplicationSuccess(true);
      
      // Show toast notification
      toast({
        title: 'Application Submitted',
        description: 'Your application has been successfully submitted!',
      });
      
      // Call the callback if provided
      if (onApplicationSubmitted) {
        onApplicationSubmitted();
      }
      
      // Open interview form after successful application
      setInterviewFormOpen(true);
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to submit application. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Apply for {job?.title || 'Position'}</DialogTitle>
            <DialogDescription>
              Submit your application for this position at {job?.profiles?.company_name || 'the company'}.
            </DialogDescription>
          </DialogHeader>
          
          {checkingStatus ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
          ) : hasApplied ? (
            <div className="py-6 text-center">
              <div className="bg-primary/10 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Application Already Submitted</h3>
              <p className="text-muted-foreground mb-4">
                You have already applied for this position. The recruiter will contact you if they are interested.
              </p>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          ) : applicationSuccess ? (
            <div className="py-6 text-center">
              <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-medium mb-2">Application Submitted Successfully!</h3>
              <p className="text-muted-foreground mb-4">
                Your application has been sent to the recruiter. You'll be notified if they want to proceed with your application.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => setInterviewFormOpen(true)}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Interview
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Application Details</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Position:</span>
                      <span className="ml-2 font-medium">{job?.title}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Company:</span>
                      <span className="ml-2 font-medium">{job?.profiles?.company_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Location:</span>
                      <span className="ml-2">{job?.location}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-2">{job?.employment_type}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="h-5 w-5 text-primary" />
                    <h3 className="font-medium">Your Profile Information</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    The following information from your profile will be shared with the employer:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <span className="ml-2">{profile?.full_name || user?.email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2">{profile?.email || user?.email}</span>
                    </div>
                    {profile?.title && (
                      <div>
                        <span className="text-muted-foreground">Title:</span>
                        <span className="ml-2">{profile.title}</span>
                      </div>
                    )}
                    {profile?.location && (
                      <div>
                        <span className="text-muted-foreground">Location:</span>
                        <span className="ml-2">{profile.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmitApplication} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Interview Scheduling Form */}
      <InterviewForm
        open={interviewFormOpen}
        onOpenChange={setInterviewFormOpen}
        applicantMode={true}
        onSuccess={() => {
          setInterviewFormOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}